import React, { FC, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';

import { useMutation } from '@apollo/react-hooks';

import { AnimatedScreen, HRule, LoadingPage } from 'Shared/components';
import {
  PartnerContext,
  getGraphqlErrors,
  getGraphqlValidationErrors,
} from 'Admin/utils';
import { PartnerType } from 'Admin/Partners/types';

import { UserForm, UserFormValues } from 'Admin/Users/components';
import { CREATE_USER, CREATE_USER_ADDRESS } from '../../graphql';
import { createUserSchema } from './validation';

export const footerErrorMessage =
  'Please ensure the data above is correct. If so, please contact growthops@clearcover.com for assistance adding this producer.';

export const CreateUserDetails: FC = () => {
  const { loading: loadingPartner, currentPartner } =
    useContext(PartnerContext);

  if (loadingPartner) return <LoadingPage />;

  return (
    <AnimatedScreen testId="create-user-details">
      <h1 data-testid="user-details-header">
        {currentPartner
          ? `Create New User of ${currentPartner.name}`
          : 'Create New User'}
      </h1>
      <HRule size="lg" />
      <CreateUserForm currentPartner={currentPartner} />
    </AnimatedScreen>
  );
};

const CreateUserForm: FC<{
  currentPartner?: PartnerType;
}> = ({ currentPartner }) => {
  const [error, setError] = useState<any>(); // store the BE error when not in validation error format; explicitly any type
  const history = useHistory();

  const [portalUserCreate] = useMutation<
    {
      portalUserCreate: {
        portalUser: {
          id: string;
        };
      };
    },
    {
      firstName: string;
      lastName: string;
      suffix: string;
      nationalProducerNumber: string;
      phoneNumber: string;
      email: string;
    }
  >(CREATE_USER);

  const [portalUserAddressCreate] = useMutation<
    {
      createOrUpdatePortalUserAddress: {
        address: {
          lineOne: string;
          lineTwo: string;
          city: string;
          zip: string;
          state: string;
        };
      };
    },
    {
      id: string;
      lineOne: string;
      lineTwo: string;
      city: string;
      zip: string;
      state: string;
    }
  >(CREATE_USER_ADDRESS);

  return (
    <Formik<UserFormValues>
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        suffix: '',
        nationalProducerNumber: '',
        phoneNumber: '',
        address: {
          lineOne: '',
          lineTwo: '',
          city: '',
          zipCode: '',
          state: '',
        },
        roles: [],
        serverValidationErrors: {},
      }}
      validateOnBlur={false}
      validationSchema={createUserSchema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting, setFieldValue, resetForm }) => {
        const scopePayload = currentPartner
          ? { scope: 'partner', scopeId: currentPartner.id }
          : { scope: 'system' };
        const { address, serverValidationErrors, ...restOfValues } = values;

        try {
          const { data } = await portalUserCreate({
            variables: {
              ...restOfValues,
              ...scopePayload,
            },
          });

          const userId = data?.portalUserCreate.portalUser.id;
          const hasAddress = Object.values(address).some((val) => !!val);

          if (userId) {
            if (hasAddress) {
              await portalUserAddressCreate({
                variables: {
                  id: userId,
                  lineOne: address.lineOne,
                  lineTwo: address.lineTwo,
                  city: address.city,
                  zip: address.zipCode,
                  state: address.state,
                },
              });
            }

            resetForm();
            const userIdUrl = currentPartner
              ? `/admin/partners/${currentPartner.id}/users/${userId}`
              : `/admin/users/${userId}`;

            history.push(userIdUrl);
          }
        } catch (err) {
          setSubmitting(false);

          const graphqlValidationErrors = getGraphqlValidationErrors(err);
          if (graphqlValidationErrors) {
            setFieldValue('serverValidationErrors', graphqlValidationErrors);
          } else {
            const otherGraphqlError = getGraphqlErrors(err);
            setError(otherGraphqlError || err.message);
          }
        }
      }}
    >
      {({ values }) => {
        const hasServerError = !!(
          values.serverValidationErrors.email ||
          values.serverValidationErrors.nationalProducerNumber
        );

        return (
          <UserForm
            type="create"
            currentPartner={currentPartner}
            footerButtonText="Send Welcome Email"
            hasValidationError={hasServerError}
            footerError={hasServerError ? footerErrorMessage : error}
          />
        );
      }}
    </Formik>
  );
};
